import { useEffect } from "react";
import { snapFrameToAlignGuides } from "../../utils/canvasAlignGuides";
import {
  clampPositionAllowOverflow,
  computeCornerUniformScale,
  computeEdgeCrop,
  computeRotationFromPointers,
  isCornerHandle,
  isSideHandle,
} from "../../components/objectTransform/objectTransformMath";
import { pointerTargetsObject as pointerTargetsObjectHit } from "./subjectHitTest";

export function useCanvasGestures(ctx) {
  const {
    alignGuideKeyRef,
    alignOthersRef,
    alphaMaskFallbackRef,
    alphaMaskRef,
    canvasSizeRef,
    clearAlignGuidesRef,
    cropExtentRef,
    cropStateRef,
    imageOverlays,
    imageUrl,
    lockObjectTransform,
    notifyObjectTransform,
    objectFlipRef,
    objectLayerVisible,
    objectPositionRef,
    objectRotationRef,
    objectSizeRef,
    objectTightBoundsRef,
    onCanvasClick,
    onObjectClick,
    onWorkspaceMarginClick,
    originalImageSizeRef,
    resizeSessionRef,
    rotateRafRef,
    rotateSessionRef,
    selectLayer,
    selectedLayerId,
    snapMovingFrameRef,
    transformRafRef,
    wasObjectTransformedRef,
    dragStart,
    isDragging,
    isResizing,
    isRotating,
    objectHovered,
    selectedElement,
    setAlignGuides,
    setCropState,
    setDragStart,
    setIsCropping,
    setIsDragging,
    setIsResizing,
    setIsRotating,
    setObjectHovered,
    setObjectPosition,
    setObjectRotation,
    setObjectSize,
    setSelectedElement,
    setWasObjectTransformed,
    showCanvasProcessingOverlay,
    wasObjectTransformed,
  } = ctx;

  const hit = {
    imageUrl,
    alphaMaskRef,
    cropStateRef,
    originalImageSizeRef,
    objectSizeRef,
    objectPositionRef,
    objectRotationRef,
    objectFlipRef,
    objectTightBoundsRef,
    alphaMaskFallbackRef,
  };
  const pointerTargetsObject = (canvasX, canvasY) => pointerTargetsObjectHit(hit, canvasX, canvasY);
  const handleCanvasPointerDown = (e) => {
    if (
      e.target.closest(".object-resize-handle") ||
      e.target.closest(".object-rotate-handle") ||
      e.target.closest(".object-transform-chrome")
    ) {
      return;
    }

    if (showCanvasProcessingOverlay) {
      return;
    }

    // Suppress the click that often follows a transform ending on the gray margin
    if (wasObjectTransformedRef.current) {
      wasObjectTransformedRef.current = false;
      setWasObjectTransformed(false);
    }

    const canvasRect = e.currentTarget.getBoundingClientRect();
    const canvasX = e.clientX - canvasRect.left;
    const canvasY = e.clientY - canvasRect.top;
    const hitObject = pointerTargetsObject(canvasX, canvasY);

    if (hitObject) {
      if (lockObjectTransform) {
        setSelectedElement(null);
        setIsDragging(false);
        return;
      }

      setSelectedElement("object");
      selectLayer("object");
      setObjectHovered(false);
      setIsDragging(true);
      setWasObjectTransformed(false);
      const pos = objectPositionRef.current;
      setDragStart({
        x: e.clientX - pos.x,
        y: e.clientY - pos.y,
      });
      if (onObjectClick) {
        onObjectClick();
      }
      return;
    }

    setIsDragging(false);
    setSelectedElement("canvas");
    selectLayer("background");
    if (onCanvasClick) {
      onCanvasClick();
    }
  };

  const handleCanvasClick = (e) => {
    if (wasObjectTransformed) {
      setWasObjectTransformed(false);
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (
      lockObjectTransform ||
      isDragging ||
      isResizing ||
      isRotating ||
      (selectedElement === "object" && selectedLayerId === "object")
    ) {
      if (objectHovered) setObjectHovered(false);
      return;
    }
    const canvasRect = e.currentTarget.getBoundingClientRect();
    const hit = pointerTargetsObject(
      e.clientX - canvasRect.left,
      e.clientY - canvasRect.top
    );
    setObjectHovered((prev) => (prev === hit ? prev : hit));
  };

  const handleCanvasMouseLeave = () => {
    if (objectHovered) setObjectHovered(false);
  };

  // Handle resize handle mouse down (object only — canvas size is preset-driven)
  const handleResizeMouseDown = (e, handle, element) => {
    if (lockObjectTransform) return;
    if (element === "canvas") {
      return;
    }

    e.stopPropagation();
    e.preventDefault();
    setSelectedElement(element);
    selectLayer("object");
    setIsResizing(true);
    setWasObjectTransformed(false);
    wasObjectTransformedRef.current = false;

    const session = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      initialSize: { ...objectSizeRef.current },
      initialPosition: { ...objectPositionRef.current },
      initialCrop: { ...cropStateRef.current },
      originalImageSize: { ...originalImageSizeRef.current },
      canvasSize: { ...canvasSizeRef.current },
    };
    resizeSessionRef.current = session;

    setIsCropping(isSideHandle(handle));

    const applyFrame = (next) => {
      if (transformRafRef.current != null) {
        cancelAnimationFrame(transformRafRef.current);
      }
      transformRafRef.current = requestAnimationFrame(() => {
        if (next.size) {
          const prev = objectSizeRef.current;
          if (
            prev.width !== next.size.width ||
            prev.height !== next.size.height
          ) {
            objectSizeRef.current = next.size;
            setObjectSize(next.size);
          }
        }
        if (next.position) {
          const prev = objectPositionRef.current;
          if (prev.x !== next.position.x || prev.y !== next.position.y) {
            objectPositionRef.current = next.position;
            setObjectPosition(next.position);
          }
        }
        if (next.crop) {
          const prev = cropStateRef.current;
          if (
            prev.x !== next.crop.x ||
            prev.y !== next.crop.y ||
            prev.width !== next.crop.width ||
            prev.height !== next.crop.height
          ) {
            cropStateRef.current = next.crop;
            setCropState(next.crop);
          }
        }
      });
    };

    const onMove = (moveEvent) => {
      if (!resizeSessionRef.current) return;
      const dx = moveEvent.clientX - session.startX;
      const dy = moveEvent.clientY - session.startY;
      if (isCornerHandle(session.handle)) {
        applyFrame(
          computeCornerUniformScale({
            handle: session.handle,
            dx,
            dy,
            initialSize: session.initialSize,
            initialPosition: session.initialPosition,
            canvasSize: session.canvasSize,
          })
        );
      } else if (isSideHandle(session.handle)) {
        applyFrame(
          computeEdgeCrop({
            handle: session.handle,
            dx,
            dy,
            initialCrop: session.initialCrop,
            initialSize: session.initialSize,
            initialPosition: session.initialPosition,
            originalImageSize: session.originalImageSize,
            cropExtent: cropExtentRef.current,
            canvasSize: session.canvasSize,
          })
        );
      }
    };

    const onUp = () => {
      if (transformRafRef.current != null) {
        cancelAnimationFrame(transformRafRef.current);
        transformRafRef.current = null;
      }
      resizeSessionRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setIsResizing(false);
      setIsCropping(false);
      setWasObjectTransformed(true);
      wasObjectTransformedRef.current = true;
      notifyObjectTransform();
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Rotation — ref + rAF path (same feel as uploaded overlay layers)
  const handleRotateMouseDown = (e) => {
    if (lockObjectTransform) return;
    e.stopPropagation();
    e.preventDefault();
    setWasObjectTransformed(false);
    wasObjectTransformedRef.current = false;
    setIsRotating(true);

    const objectElement =
      e.currentTarget.closest(".object-transform-chrome") ||
      e.currentTarget.closest(".object-wrapper");
    const rect = objectElement.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const startAngle =
      Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);

    rotateSessionRef.current = {
      centerX,
      centerY,
      startAngle,
      initialRotation: objectRotationRef.current,
    };

    const onMove = (moveEvent) => {
      const rotSession = rotateSessionRef.current;
      if (!rotSession) return;
      const newRotation = computeRotationFromPointers({
        clientX: moveEvent.clientX,
        clientY: moveEvent.clientY,
        centerX: rotSession.centerX,
        centerY: rotSession.centerY,
        startAngle: rotSession.startAngle,
        initialRotation: rotSession.initialRotation,
        shiftKey: moveEvent.shiftKey,
      });

      if (rotateRafRef.current != null) {
        cancelAnimationFrame(rotateRafRef.current);
      }
      rotateRafRef.current = requestAnimationFrame(() => {
        objectRotationRef.current = newRotation;
        setObjectRotation(newRotation);
      });
    };

    const onUp = () => {
      if (rotateRafRef.current != null) {
        cancelAnimationFrame(rotateRafRef.current);
        rotateRafRef.current = null;
      }
      rotateSessionRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setIsRotating(false);
      setWasObjectTransformed(true);
      wasObjectTransformedRef.current = true;
      notifyObjectTransform();
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Shift+rotate snaps via moveEvent.shiftKey on the rotate session

  alignOthersRef.current = imageOverlays
    .filter((layer) => layer.visible !== false)
    .map((layer) => ({
      id: layer.id,
      x: layer.x,
      y: layer.y,
      width: layer.width,
      height: layer.height,
      rotation: layer.rotation,
    }));

  const publishAlignGuides = (guides) => {
    const key = `${guides.vertical.join(",")}|${guides.horizontal.join(",")}`;
    if (alignGuideKeyRef.current === key) return;
    alignGuideKeyRef.current = key;
    setAlignGuides(guides);
  };

  const clearAlignGuides = () => {
    publishAlignGuides({ vertical: [], horizontal: [] });
  };

  const snapMovingFrame = (frame, excludeId) => {
    const others = [];
    if (excludeId !== "object" && objectLayerVisible && objectSizeRef.current.width > 0) {
      others.push({
        x: objectPositionRef.current.x,
        y: objectPositionRef.current.y,
        width: objectSizeRef.current.width,
        height: objectSizeRef.current.height,
        rotation: objectRotationRef.current,
      });
    }
    for (const layer of alignOthersRef.current) {
      if (layer.id !== excludeId) others.push(layer);
    }
    const canvas = canvasSizeRef.current;
    const snapped = snapFrameToAlignGuides(
      frame,
      others,
      canvas.width,
      canvas.height
    );
    publishAlignGuides(snapped.guides);
    return snapped;
  };
  snapMovingFrameRef.current = snapMovingFrame;
  clearAlignGuidesRef.current = clearAlignGuides;

  // Drag only — resize/rotate bind their own window listeners
  useEffect(() => {
    if (!isDragging) return undefined;

    const onMove = (e) => {
      if (transformRafRef.current != null) {
        cancelAnimationFrame(transformRafRef.current);
      }
      const size = objectSizeRef.current;
      const canvas = canvasSizeRef.current;
      const start = dragStart;
      const snapped = snapMovingFrameRef.current(
        {
          x: e.clientX - start.x,
          y: e.clientY - start.y,
          width: size.width,
          height: size.height,
          rotation: objectRotationRef.current,
        },
        "object"
      );
      const next = clampPositionAllowOverflow(
        snapped.x,
        snapped.y,
        size.width,
        size.height,
        canvas.width,
        canvas.height
      );
      transformRafRef.current = requestAnimationFrame(() => {
        const prev = objectPositionRef.current;
        if (prev.x === next.x && prev.y === next.y) return;
        objectPositionRef.current = next;
        setObjectPosition(next);
      });
    };
    const onUp = () => {
      if (transformRafRef.current != null) {
        cancelAnimationFrame(transformRafRef.current);
        transformRafRef.current = null;
      }
      clearAlignGuidesRef.current?.();
      setIsDragging(false);
      setWasObjectTransformed(true);
      wasObjectTransformedRef.current = true;
      notifyObjectTransform();
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging, dragStart]);

  // Deselect only when clicking the gray workspace margin — not sidebar/panels (that hid handles while Object tab stayed active).
  useEffect(() => {
    const handleWindowClick = (e) => {
      // Transform gestures often end with a click on the gray margin — ignore that one
      if (wasObjectTransformedRef.current) {
        wasObjectTransformedRef.current = false;
        setWasObjectTransformed(false);
        return;
      }
      if (!e.target.closest(".canvas-editor-container")) {
        return;
      }
      if (
        e.target.closest(".canvas-element") ||
        e.target.closest(".object-resize-handle") ||
        e.target.closest(".object-rotate-handle") ||
        e.target.closest(".object-transform-chrome") ||
        e.target.closest(".overlay-image-node--chrome")
      ) {
        return;
      }
      setSelectedElement("canvas");
      if (typeof onWorkspaceMarginClick === "function") {
        onWorkspaceMarginClick();
      }
    };

    window.addEventListener("click", handleWindowClick);
    return () => window.removeEventListener("click", handleWindowClick);
  }, [onWorkspaceMarginClick]);

  return {
    handleCanvasPointerDown,
    handleCanvasClick,
    handleCanvasMouseMove,
    handleCanvasMouseLeave,
    handleResizeMouseDown,
    handleRotateMouseDown,
    snapMovingFrame,
    clearAlignGuides,
  };
}
